import AiContactMemory from '../../AiChannel/AiContactMemory';
import { CustomerProfileButton } from "src/common/customerSharing/CustomerSharing";
import React, { useState } from "react";
import {
  X,
  Phone,
  Mail,
  Hash,
  Globe2,
  ShieldCheck,
  StickyNote,
  Clock3,
  Pencil,
  MessageCircleMore,
  Copy,
  Check,
  Ban,
} from "lucide-react";

import { ContactAvatar } from "src/components/ContactAvtar";
import { formatRelativeTime, formatFullDateTime } from "src/utils/DateTimeFormatter";
import ContactTagsSection from "./ContactTagsSection";

/** "signup_date" -> "Signup Date"; keeps all-caps tokens (ID, URL) intact. */
export function humanizeKey(key = "") {
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .map((w) => (w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(String(value));
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
      className="rounded-md p-1 text-text-muted transition hover:bg-theme-50 hover:text-primary dark:text-dark-text-muted dark:hover:bg-dark-soft-blue"
      aria-label="Copy"
    >
      {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
    </button>
  );
}

function Field({ icon: Icon, label, value, copyable }) {
  return (
    <div className="flex items-start gap-2.5 py-2">
      {Icon && (
        <Icon size={13} className="mt-0.5 shrink-0 text-text-muted dark:text-dark-text-muted" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted dark:text-dark-text-muted">
          {label}
        </p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <p className="min-w-0 break-words text-xs font-medium text-text-primary dark:text-dark-text-primary">
            {value || <span className="text-text-muted">—</span>}
          </p>
          {copyable && value ? <CopyButton value={value} /> : null}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-primary dark:text-dark-primary">
        {title}
      </p>
      <div className="divide-y divide-border-light dark:divide-dark-border-light">
        {children}
      </div>
    </div>
  );
}

function ContactDetailModal({
  open = false,
  contact = null,
  onClose,
  onEdit,
  onMessage,
  onTagsChanged,
}) {
  if (!open || !contact) return null;

  const metadata =
    contact.metadata && typeof contact.metadata === "object" ? contact.metadata : {};
  const metadataEntries = Object.entries(metadata).filter(
    ([, v]) => v !== null && v !== undefined && String(v).trim() !== "",
  );

  const activity = [
    ["Created", contact.created_at],
    ["Last updated", contact.updated_at],
    ["Last seen", contact.last_seen_at],
    ["Last message", contact.last_message_at],
  ].filter(([, v]) => v);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-[#0f2b42]/25 backdrop-blur-[4px]"
        onClick={onClose}
      />

      <div className="relative z-10 flex max-h-[92vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl border border-border-color bg-card-bg shadow-elevated dark:border-dark-border dark:bg-dark-card">
        {/* HEADER */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border-light px-5 py-4 dark:border-dark-border-light">
          <div className="flex min-w-0 items-center gap-3">
            <ContactAvatar contact={contact?.name} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="truncate text-sm font-bold text-text-primary dark:text-dark-text-primary">
                  {contact.name || "Unknown contact"}
                </h2>
                {contact.is_blocked && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[9px] font-bold text-danger dark:bg-red-950/30">
                    <Ban size={9} /> Blocked
                  </span>
                )}
              </div>
              <p className="truncate text-[11px] text-text-muted dark:text-dark-text-muted">
                {contact.phone_number || "No number"}
                {contact.status ? ` · ${contact.status}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition hover:bg-theme-50 hover:text-primary dark:text-dark-text-muted dark:hover:bg-dark-soft-blue"
          >
            <X size={16} />
          </button>
        </div>

        {/* BODY */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <CustomerProfileButton recordId={contact.id} name={contact.name} />
          <Section title="Tags">
            <div className="py-1">
              <ContactTagsSection contact={contact} onChanged={onTagsChanged} />
            </div>
          </Section>

          <AiContactMemory contactId={contact.id}/>
          <Section title="Contact">
            <Field icon={Phone} label="Phone number" value={contact.phone_number} copyable />
            <Field icon={Hash} label="Country code" value={contact.country_code} />
            <Field icon={Mail} label="Email" value={contact.email} copyable />
            <Field icon={MessageCircleMore} label="WhatsApp ID" value={contact.wa_id} copyable />
            <Field icon={ShieldCheck} label="Status" value={contact.status} />
          </Section>

          {(contact.profile_picture_url || contact.notes) && (
            <Section title="Profile">
              {contact.profile_picture_url && (
                <div className="py-2">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-muted dark:text-dark-text-muted">
                    Profile picture
                  </p>
                  <img
                    src={contact.profile_picture_url}
                    alt=""
                    className="h-20 w-20 rounded-xl border border-border-color object-cover dark:border-dark-border"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
              {contact.notes && (
                <Field icon={StickyNote} label="Notes" value={contact.notes} />
              )}
            </Section>
          )}

          <Section title={`Custom fields${metadataEntries.length ? ` (${metadataEntries.length})` : ""}`}>
            {metadataEntries.length === 0 ? (
              <p className="py-2 text-[11px] text-text-muted dark:text-dark-text-muted">
                No extra fields. Columns you don’t map during an import are saved here.
              </p>
            ) : (
              metadataEntries.map(([key, value]) => (
                <Field
                  key={key}
                  icon={Globe2}
                  label={humanizeKey(key)}
                  value={String(value)}
                  copyable
                />
              ))
            )}
          </Section>

          {activity.length > 0 && (
            <Section title="Activity">
              {activity.map(([label, value]) => (
                <div key={label} className="flex items-start gap-2.5 py-2">
                  <Clock3 size={13} className="mt-0.5 shrink-0 text-text-muted dark:text-dark-text-muted" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted dark:text-dark-text-muted">
                      {label}
                    </p>
                    <p
                      className="mt-0.5 text-xs font-medium text-text-primary dark:text-dark-text-primary"
                      title={formatFullDateTime(value)}
                    >
                      {formatRelativeTime(value)}
                    </p>
                  </div>
                </div>
              ))}
            </Section>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border-light bg-card-bg/95 px-5 py-3 dark:border-dark-border-light dark:bg-dark-card/95">
          {onMessage && (
            <button
              type="button"
              onClick={() => onMessage(contact)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border-color bg-card-bg px-3 py-2 text-xs font-semibold text-text-secondary transition hover:bg-theme-50 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary"
            >
              <MessageCircleMore size={14} />
              Message
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(contact)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white transition hover:bg-primary/90"
            >
              <Pencil size={14} />
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContactDetailModal;
